import React from "react";
import Home from "../Home/Home";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { Storage } from "aws-amplify";
import { localStoragePut } from "../../util/localStorage";
import {
  addMusicAsset,
  getMusicAsset,
  deleteMusicAsset,
  getPublicMusicAsset,
} from "../../Redux/Actions/asset";
import "./style.css";

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const storagePut = (key: string, file: any, opts: any): Promise<any> =>
  isLocalAuth ? localStoragePut(key, file, opts) : Storage.put(key, file, opts);

interface IProps {
  musicAssets: any[];
  publicMusic: any[];
  addMusicAsset: (asset: any) => void;
  getMusicAsset: () => void;
  deleteMusicAsset: (assetId: any) => void;
  getPublicMusicAsset: () => void;
  loadingAssets: boolean;
}

class MusicAssets extends React.Component<IProps> {
  fileRef: HTMLInputElement | null = null;

  state = {
    activeTab: "private" as "private" | "public",
    musicFile: null as File | null,
    musicTitle: "",
    uploading: false,
  };

  componentDidMount() {
    this.props.getMusicAsset();
    this.props.getPublicMusicAsset();
  }

  handleFileSelect = (e: any) => {
    const audioTypes = /(\.|\/)(mp3|ogg|wav|m4a|aac|flac)$/i;
    const file = e.target.files?.[0];
    if (!file) return;
    if (!audioTypes.test(file.name)) {
      toast.error("Please upload a valid audio file (mp3, ogg, wav, m4a, aac, flac)");
      return;
    }
    this.setState({ musicFile: file });
  };

  handleUpload = () => {
    const { musicFile, musicTitle } = this.state;
    if (!musicFile) { toast.error("Please select a music file"); return; }
    if (!musicTitle.trim()) { toast.error("Please enter a title for your music"); return; }

    this.setState({ uploading: true });
    const key = `${uuid()}-music`;
    storagePut(key, musicFile, { level: "protected", contentType: musicFile.type })
      .then((response: any) => {
        this.props.addMusicAsset({ url: response.key, title: musicTitle.trim() });
        this.setState({ musicFile: null, musicTitle: "", uploading: false });
        if (this.fileRef) this.fileRef.value = "";
      })
      .catch((err: any) => {
        console.error("Error uploading music:", err);
        toast.error("Failed to upload music. Please try again.");
        this.setState({ uploading: false });
      });
  };

  handleDelete = (asset: any) => {
    if (window.confirm("Are you sure you want to delete this music?")) {
      this.props.deleteMusicAsset(asset._id);
    }
  };

  render() {
    const { musicAssets, publicMusic, loadingAssets } = this.props;
    const { activeTab, musicFile, musicTitle, uploading } = this.state;

    return (
      <Home>
        <div className="maPage">
          <div className="maHeader">
            <h2 className="maTitle">Music Library</h2>
            <p className="maSubtitle">Upload and manage music for your videos</p>
          </div>

          {/* Upload Section */}
          <div className="maUploadCard">
            <h3 className="maUploadTitle">Upload Music</h3>
            <div className="maUploadForm">
              <div className="maFormGroup">
                <label className="maFormLabel">Title</label>
                <input
                  type="text"
                  className="maFormInput"
                  placeholder="Enter a name for your music"
                  value={musicTitle}
                  onChange={(e) => this.setState({ musicTitle: e.target.value })}
                />
              </div>
              <div className="maFormGroup">
                <label className="maFormLabel">Audio File</label>
                <div className="maFileRow">
                  <button
                    className="maChooseFileBtn"
                    onClick={() => this.fileRef?.click()}
                  >
                    Choose File
                  </button>
                  <span className="maFileName">
                    {musicFile ? musicFile.name : "No file selected"}
                  </span>
                  <input
                    ref={(ref) => (this.fileRef = ref)}
                    type="file"
                    accept="audio/*"
                    style={{ display: "none" }}
                    onChange={this.handleFileSelect}
                  />
                </div>
              </div>
              <button
                className="maUploadBtn"
                onClick={this.handleUpload}
                disabled={uploading || !musicFile || !musicTitle.trim()}
              >
                {uploading ? "Uploading..." : "Upload Music"}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="maTabs">
            <button
              className={`maTab ${activeTab === "private" ? "maTabActive" : ""}`}
              onClick={() => this.setState({ activeTab: "private" })}
            >
              My Music
            </button>
            <button
              className={`maTab ${activeTab === "public" ? "maTabActive" : ""}`}
              onClick={() => this.setState({ activeTab: "public" })}
            >
              Public Music
            </button>
          </div>

          {/* Music List */}
          <div className="maMusicList">
            {loadingAssets && <div className="maLoading">Loading...</div>}

            {activeTab === "private" && (
              <>
                {(!musicAssets || musicAssets.length === 0) && !loadingAssets && (
                  <div className="maEmpty">
                    <p>No music uploaded yet. Upload your first track above.</p>
                  </div>
                )}
                {musicAssets &&
                  musicAssets.map((asset: any) => (
                    <div className="maMusicCard" key={asset._id}>
                      <div className="maMusicInfo">
                        <h4 className="maMusicTitle">{asset.title || "Untitled"}</h4>
                        <audio
                          src={asset.signedUrl || asset.url}
                          controls
                          className="maMusicPlayer"
                        />
                      </div>
                      <button
                        className="maMusicDeleteBtn"
                        onClick={() => this.handleDelete(asset)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
              </>
            )}

            {activeTab === "public" && (
              <>
                {(!publicMusic || publicMusic.length === 0) && !loadingAssets && (
                  <div className="maEmpty">
                    <p>No public music available.</p>
                  </div>
                )}
                {publicMusic &&
                  publicMusic.map((asset: any) => (
                    <div className="maMusicCard" key={asset._id}>
                      <div className="maMusicInfo">
                        <h4 className="maMusicTitle">{asset.title || "Untitled"}</h4>
                        <audio
                          src={asset.signedUrl || asset.url}
                          controls
                          className="maMusicPlayer"
                        />
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      </Home>
    );
  }
}

const mapStateToProps = (state: any) => ({
  musicAssets: state.asset.musicAssets,
  publicMusic: state.asset.publicAssets,
  loadingAssets: state.asset.loadingAssets,
});
const mapDispatchToProps = (dispatch: any) => ({
  addMusicAsset: (asset: any) => dispatch(addMusicAsset(asset)),
  getMusicAsset: () => dispatch(getMusicAsset()),
  deleteMusicAsset: (assetId: any) => dispatch(deleteMusicAsset(assetId)),
  getPublicMusicAsset: () => dispatch(getPublicMusicAsset()),
});
export default connect(mapStateToProps, mapDispatchToProps)(MusicAssets);
